import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Save, Search, Settings2 } from "lucide-react";

import api from "../api";
import { clearMasterDataCache, useMasterData } from "../../utils/useMasterData";

const TABS = [
  { id: "categories", label: "Categories", endpoint: "/admin/categories" },
  { id: "families", label: "Families", endpoint: "/admin/families" },
  { id: "machines", label: "Machines", endpoint: "/admin/machines" },
  { id: "units", label: "Mfg Units", endpoint: "/admin/manufacturing-units" },
  { id: "payroll", label: "Payroll Components", endpoint: "/admin/payroll-components" },
];

const blankForms = {
  categories: { name: "", code: "", description: "", is_active: true },
  families: { name: "", code: "", description: "", is_active: true },
  machines: { name: "", machine_code: "", machine_type: "", branch_id: "", status: "active", description: "", last_maintenance_date: "", next_maintenance_date: "" },
  units: { name: "", code: "", branch_id: "", address: "", contact_person: "", phone: "", is_active: true },
  payroll: { code: "", name: "", component_type: "EARNING", is_taxable: true, is_statutory: false, formula: "", default_value: 0, is_active: true },
};

const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, boxShadow: "0 10px 22px rgba(15,23,42,.06)" };
const input = { width: "100%", borderRadius: 10, border: "1px solid #cbd5e1", padding: "10px 12px", background: "#fff" };
const btn = { display: "inline-flex", alignItems: "center", gap: 8, borderRadius: 10, border: "1px solid #cbd5e1", padding: "10px 14px", background: "#fff", color: "#0f172a", fontWeight: 700, cursor: "pointer" };
const primaryBtn = { ...btn, background: "#4f46e5", color: "#fff", border: "none" };

export default function ProductionMasterSetupView() {
  const [activeTab, setActiveTab] = useState("categories");
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [form, setForm] = useState(blankForms.categories);
  const { getOptions } = useMasterData("MACHINE_TYPE");

  const loadRows = useCallback(async (tabId = activeTab) => {
    const tab = TABS.find((item) => item.id === tabId);
    if (!tab) return;
    setLoading(true);
    try {
      const [dataRes, branchRes] = await Promise.all([
        api.get(tab.endpoint),
        api.get("/admin/branches").catch(() => ({ data: { data: [] } })),
      ]);
      setRows(dataRes.data?.data || []);
      setBranches(branchRes.data?.data || []);
      setMessage({ type: "", text: "" });
    } catch (err) {
      setRows([]);
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to load setup data." });
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { setForm(blankForms[activeTab]); loadRows(activeTab); }, [activeTab, loadRows]);

  const filteredRows = useMemo(() => rows.filter((row) => JSON.stringify(row).toLowerCase().includes(search.trim().toLowerCase())), [rows, search]);

  const saveNew = async () => {
    const tab = TABS.find((item) => item.id === activeTab);
    if (!tab) return;
    setSaving(true);
    try {
      await api.post(tab.endpoint, form);
      if (activeTab === "machines") clearMasterDataCache("MACHINE_TYPE");
      setForm(blankForms[activeTab]);
      await loadRows(activeTab);
      setMessage({ type: "success", text: "Record created successfully." });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to create record." });
    } finally {
      setSaving(false);
    }
  };

  const updateRow = async (row, patch = {}) => {
    const endpointMap = {
      categories: `/admin/categories/${row.id}`,
      families: `/admin/families/${row.id}`,
      machines: `/admin/machines/${row.id}`,
      units: `/admin/manufacturing-units/${row.id}`,
      payroll: `/admin/payroll-components/${row.id}`,
    };
    setSaving(true);
    try {
      await api.put(endpointMap[activeTab], patch);
      await loadRows(activeTab);
      setMessage({ type: "success", text: "Record updated." });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to update record." });
    } finally {
      setSaving(false);
    }
  };

  const deactivateRow = async (row) => {
    const endpointMap = {
      categories: `/admin/categories/${row.id}`,
      families: `/admin/families/${row.id}`,
      machines: `/admin/machines/${row.id}`,
      units: `/admin/manufacturing-units/${row.id}`,
      payroll: `/admin/payroll-components/${row.id}`,
    };
    if (!window.confirm("Deactivate this record?")) return;
    setSaving(true);
    try {
      await api.delete(endpointMap[activeTab]);
      await loadRows(activeTab);
      setMessage({ type: "success", text: "Record deactivated." });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to deactivate record." });
    } finally {
      setSaving(false);
    }
  };

  const seedDefaults = async () => {
    setSaving(true);
    try {
      await api.post("/admin/seed-all-defaults");
      clearMasterDataCache();
      await loadRows(activeTab);
      setMessage({ type: "success", text: "Defaults seeded." });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to seed defaults." });
    } finally {
      setSaving(false);
    }
  };

  const renderForm = () => {
    if (activeTab === "machines") {
      const machineTypes = getOptions("MACHINE_TYPE");
      return (
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
          <input style={input} placeholder="Name" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
          <input style={input} placeholder="Code" value={form.machine_code} onChange={(e) => setForm((prev) => ({ ...prev, machine_code: e.target.value }))} />
          <select style={input} value={form.machine_type} onChange={(e) => setForm((prev) => ({ ...prev, machine_type: e.target.value }))}><option value="">Machine Type</option>{machineTypes.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
          <select style={input} value={form.branch_id} onChange={(e) => setForm((prev) => ({ ...prev, branch_id: e.target.value }))}><option value="">Branch</option>{branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}</select>
          <select style={input} value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}><option value="active">active</option><option value="inactive">inactive</option><option value="maintenance">maintenance</option></select>
          <input style={input} placeholder="Description" value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
        </div>
      );
    }
    if (activeTab === "units") {
      return (
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
          <input style={input} placeholder="Name" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
          <input style={input} placeholder="Code" value={form.code} onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))} />
          <select style={input} value={form.branch_id} onChange={(e) => setForm((prev) => ({ ...prev, branch_id: e.target.value }))}><option value="">Branch</option>{branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}</select>
          <input style={input} placeholder="Address" value={form.address} onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))} />
          <input style={input} placeholder="Contact Person" value={form.contact_person} onChange={(e) => setForm((prev) => ({ ...prev, contact_person: e.target.value }))} />
          <input style={input} placeholder="Phone" value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} />
        </div>
      );
    }
    if (activeTab === "payroll") {
      return (
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
          <input style={input} placeholder="Code" value={form.code} onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))} />
          <input style={input} placeholder="Name" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
          <select style={input} value={form.component_type} onChange={(e) => setForm((prev) => ({ ...prev, component_type: e.target.value }))}><option value="EARNING">EARNING</option><option value="DEDUCTION">DEDUCTION</option></select>
          <input style={input} type="number" placeholder="Default Value" value={form.default_value} onChange={(e) => setForm((prev) => ({ ...prev, default_value: e.target.value }))} />
          <input style={{ ...input, gridColumn: "span 4" }} placeholder="Formula" value={form.formula} onChange={(e) => setForm((prev) => ({ ...prev, formula: e.target.value }))} />
        </div>
      );
    }
    return (
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr 2fr" }}>
        <input style={input} placeholder="Name" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
        <input style={input} placeholder="Code" value={form.code} onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))} />
        <input style={input} placeholder="Description" value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
      </div>
    );
  };

  const renderColumns = (row) => {
    if (activeTab === "machines") return [row.name, row.machine_code, row.machine_type, row.branch_name || row.branch_id || "-", row.status];
    if (activeTab === "units") return [row.name, row.code, row.branch_name || row.branch_id || "-", row.contact_person || "-", row.phone || "-", row.is_active ? "Active" : "Inactive"];
    if (activeTab === "payroll") return [row.code, row.name, row.component_type, row.is_taxable ? "Yes" : "No", row.is_statutory ? "Yes" : "No", row.formula || "-", row.default_value];
    return [row.name, row.code, row.description || "-", row.is_active ? "Active" : "Inactive"];
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={card}>
        <div style={{ padding: 18, borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0f172a" }}>Production Master Setup</h2>
            <p style={{ margin: "6px 0 0", color: "#64748b" }}>Configure complex production and payroll setup tables with admin CRUD.</p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={btn} onClick={() => loadRows(activeTab)}><RefreshCw size={16} /> Refresh</button>
            <button style={primaryBtn} onClick={seedDefaults}><Settings2 size={16} /> Seed Defaults</button>
          </div>
        </div>
        {message.text ? <div style={{ margin: 18, borderRadius: 12, padding: "12px 14px", background: message.type === "error" ? "#fef2f2" : "#ecfdf5", color: message.type === "error" ? "#b91c1c" : "#166534", border: `1px solid ${message.type === "error" ? "#fecaca" : "#bbf7d0"}` }}>{message.text}</div> : null}
        <div style={{ padding: 18, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {TABS.map((tab) => (
            <button key={tab.id} style={{ ...btn, background: activeTab === tab.id ? "#eef2ff" : "#fff", color: activeTab === tab.id ? "#4338ca" : "#0f172a", borderColor: activeTab === tab.id ? "#818cf8" : "#cbd5e1" }} onClick={() => setActiveTab(tab.id)}>{tab.label}</button>
          ))}
        </div>
        <div style={{ padding: "0 18px 18px", display: "grid", gap: 14 }}>
          <div style={{ ...card, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 800, color: "#0f172a" }}>Add New</div>
              <button style={primaryBtn} onClick={saveNew} disabled={saving}><Plus size={16} /> Add New</button>
            </div>
            {renderForm()}
          </div>
          <div style={{ ...card, overflow: "hidden" }}>
            <div style={{ padding: 16, borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 8 }}>
              <Search size={16} color="#64748b" />
              <input style={input} placeholder="Search records" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {loading ? (
                    <tr><td style={{ padding: 18, color: "#64748b" }}>Loading...</td></tr>
                  ) : filteredRows.length === 0 ? (
                    <tr><td style={{ padding: 18, color: "#64748b" }}>No data. Click Seed Defaults to create standard entries.</td></tr>
                  ) : filteredRows.map((row) => (
                    <tr key={row.id} style={{ borderTop: "1px solid #e2e8f0" }}>
                      {renderColumns(row).map((value, index) => <td key={`${row.id}-${index}`} style={{ padding: 12 }}>{String(value ?? "-")}</td>)}
                      <td style={{ padding: 12, textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                          <button style={btn} onClick={() => updateRow(row, activeTab === "machines" ? { status: row.status === "active" ? "inactive" : "active" } : { is_active: !row.is_active })}><Save size={14} /> {activeTab === "machines" ? "Toggle Status" : "Toggle Active"}</button>
                          <button style={btn} onClick={() => deactivateRow(row)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
